import React, { FC } from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { generateItemImageUrl, getItemQuantity } from '../../utils/general';
import {
  getOrderDetailsLineItems,
  getLineItemName,
  getLineItemUnitPrice,
  IOpenedFrom,
} from '../../utils/order-details';
import { UNAVAILABLE_IMAGE, ITEM_DESCRIPTION_UNAVAILABLE } from '../../constants';
import { LineItemPrice } from './LineItemPrice';
import { IOrderDetails, IOrderDetailsLineItem } from '../../models/order-details';

interface FulfillmentLineItemsProps {
  orderDetails?: IOrderDetails;
  /**
   * The card the drawer opened from. A mixed order carries a pickup group and
   * a delivery group at once, and only the card says which of them applies.
   */
  distribution?: IOpenedFrom;
}

/**
 * A blank field is an absent one. The catalog sends `''` as readily as it
 * sends null, and a `??` chain keeps the empty string — which rendered a blank
 * description next to an `alt`/`title` that correctly said "unavailable".
 */
const presentText = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export const FulfillmentLineItems: FC<FulfillmentLineItemsProps> = ({
  orderDetails,
  distribution,
}) => {
  const lineItems = getOrderDetailsLineItems(orderDetails, distribution);

  if (!lineItems.length) {
    return null;
  }

  return (
    <>
      {lineItems.map((lineItem: IOrderDetailsLineItem, index: number) => {
        // #1897 stops the server falling `quantity` back to `currentQuantity`.
        const itemQuantity = getItemQuantity(lineItem);
        const unitPrice = getLineItemUnitPrice(lineItem);
        const itemName = presentText(getLineItemName(lineItem));
        const brand = presentText(lineItem?.brandName);

        /**
         * The design runs the brand and the description together as one wrapped
         * block, brand bold. `activeOrders` had no brand, which is why the old
         * comment here claimed the name carried the whole description.
         *
         * The placeholder stands in for the description only, so the brand
         * still shows when it is the one thing the order does know. `title`
         * is built from the same two pieces as the visible text, so the
         * tooltip and the accessible name always read what is on screen.
         */
        const description = itemName ?? ITEM_DESCRIPTION_UNAVAILABLE;
        const itemDetails = (
          <span>
            {brand && <span className="sui-font-bold">{brand} </span>}
            {description}
          </span>
        );

        return (
          <React.Fragment key={lineItem?.lineId ?? index}>
            <div
              className="sui-flex sui-flex-row sui-justify-between sui-pl-2 sui-py-2"
              data-testid="fulfillment-line-item"
            >
              <div className="sui-flex sui-flex-row sui-gap-4">
                <img
                  src={generateItemImageUrl(lineItem)}
                  alt={itemName ?? UNAVAILABLE_IMAGE}
                  width="50"
                  height="50"
                />
                <div className="sui-max-w-32">
                  <Typography
                    variant="body-sm"
                    lineClamp={2}
                    title={[brand, description].filter(Boolean).join(' ')}
                  >
                    {itemDetails}
                  </Typography>
                </div>
              </div>
              <div className="sui-flex sui-flex-col sui-items-end">
                {/*
                  `LineItemPrice` defaults a missing quantity to 1 and a
                  missing unit price to 0, so either gap alone is enough to
                  print a price the order never quoted. No price beats a
                  wrong one, and a quantity-less item gets no "Qty:" label
                  rather than a dangling one.
                */}
                {itemQuantity != null && unitPrice != null && (
                  <Typography variant="h6" weight="bold">
                    <LineItemPrice quantity={itemQuantity} unitPrice={unitPrice} />
                  </Typography>
                )}
                {itemQuantity != null && (
                  <Typography variant="body-sm">Qty: {itemQuantity}</Typography>
                )}
              </div>
            </div>
            {index < lineItems.length - 1 && <hr className="sui-mb-4" />}
          </React.Fragment>
        );
      })}
    </>
  );
};
